import React, { useState, useEffect } from 'react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../layouts/MainLayout';
import { apiService } from '../services/api';

interface CallStats {
  total_calls: number;
  completed_calls: number;
  missed_calls: number;
  total_duration: number;
  average_duration: number;
}

interface CallLog {
  id: number;
  agent_id: number;
  phone_number: string;
  contact_name: string | null;
  start_time: string;
  end_time: string | null;
  duration: number | null;
  status: string;
  notes: string | null;
  agent: {
    name: string;
  };
}

const Reports: React.FC = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState<CallStats | null>(null);
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [agentId, setAgentId] = useState<string>('');

  const fetchStats = async () => {
    if (!token) {
      return;
    }
    
    setLoading(true);
    try {
      const params: Record<string, any> = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (agentId) params.agent_id = agentId;

      const response = await apiService.get<{ data: CallStats }>('call-logs/stats', params);
      if (response.success && response.data) {
        setStats(response.data as any);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCallLogs = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const params: Record<string, any> = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (agentId) params.agent_id = agentId;

      const response = await apiService.get<{ data: { items: CallLog[] } }>('call-logs', params);
      if (response.success && response.data && Array.isArray((response.data as any).items)) {
        setCallLogs((response.data as any).items);
      } else {
        setCallLogs([]);
      }
    } catch (error) {
      console.error('Error fetching call logs:', error);
      setCallLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchCallLogs();
  }, [token, dateFrom, dateTo, agentId]);

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'missed':
        return 'text-red-600';
      case 'rejected':
        return 'text-orange-600';
      case 'in_progress':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <MainLayout>
      <div className="p-6">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Call Reports
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            View call analytics and performance metrics
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex gap-4">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateFrom(e.target.value)}
                className="w-40"
              />
              <Input
                type="date"
                value={dateTo}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateTo(e.target.value)}
                className="w-40"
              />
              <Input
                placeholder="Agent ID (optional)"
                value={agentId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAgentId(e.target.value)}
                className="w-40"
              />
            </div>
            <Button onClick={fetchStats} disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Calls</h3>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total_calls}</div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Completed</h3>
              <div className="text-2xl font-bold text-green-600">{stats.completed_calls}</div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Missed</h3>
              <div className="text-2xl font-bold text-red-600">{stats.missed_calls}</div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Duration</h3>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatDuration(stats.total_duration)}</div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Duration</h3>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatDuration(Math.round(stats.average_duration))}</div>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Call Logs</h2>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent</TableHead>
                    <TableHead>Phone Number</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Start Time</TableHead>
                    <TableHead>End Time</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {callLogs.map((call) => (
                    <TableRow key={call.id}>
                      <TableCell>{call.agent?.name || 'Unknown'}</TableCell>
                      <TableCell>{call.phone_number}</TableCell>
                      <TableCell>{call.contact_name || '-'}</TableCell>
                      <TableCell>{formatDate(call.start_time)}</TableCell>
                      <TableCell>
                        {call.end_time ? formatDate(call.end_time) : '-'}
                      </TableCell>
                      <TableCell>
                        {call.duration ? formatDuration(call.duration) : '-'}
                      </TableCell>
                      <TableCell>
                        <span className={getStatusColor(call.status)}>
                          {call.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </TableCell>
                      <TableCell>{call.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Reports; 